import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'antd';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { PeriodFilter } from 'src/components/domain/metrics/period-filter';
import { FlexRow } from 'src/components/layout/flex-row';
import { PageContainer } from 'src/components/layout/page-containers';
import { AgentPerformanceCard } from 'src/components/pages/dashboard/components/cards/agent-performance-card';
import { AssignedContactsCard } from 'src/components/pages/dashboard/components/cards/assigned-contacts-card';
import { ClosedContactsCard } from 'src/components/pages/dashboard/components/cards/closed-contacts-card';
import { MedianResponseTimeCard } from 'src/components/pages/dashboard/components/cards/median-response-time';
import { NewContactAcquisitionCard } from 'src/components/pages/dashboard/components/cards/new-contact-acquisition-card';
import { NewContactsCard } from 'src/components/pages/dashboard/components/cards/new-contacts-card';
import { NextAppointmentsCard } from 'src/components/pages/dashboard/components/cards/next-appointments-card';
import { NextTasksCard } from 'src/components/pages/dashboard/components/cards/next-tasks-card';
import { SummaryAnsweredConversationsCard } from 'src/components/pages/dashboard/components/cards/summary-answered-conversations-card';
import { UnansweredConversationsCard } from 'src/components/pages/dashboard/components/cards/unanswered-conversations-card';
import { UnassignedContactsCard } from 'src/components/pages/dashboard/components/cards/unassigned-contacts-card';
import { DashboardFiltersContextProvider } from 'src/components/pages/dashboard/components/filters/dashboard-filters-context';
import { DashboardUserFilter } from 'src/components/pages/dashboard/components/filters/dashboard-user-filter';
import { useAddBreadcrumb } from 'src/hooks/use-add-breadcrumb';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { BreadcrumbBetaBadge } from '../conversations/components/breadcrumb-beta-badge';
import { CallAnalysisCard } from './components/cards/call-analysis-card';
import { ContactsToFollowUpCard } from './components/cards/contacts-to-follow-up-card';

const FiltersContainer = styled(FlexRow)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.koala};
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 20px;

  ${up('md')} {
    justify-content: space-between;
    margin-bottom: 30px;
    padding: 0 0 30px;
  }
`;

const CardsContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 20px 15px 20px;

  ${up('md')} {
    background: transparent;
    padding: 0;
  }
`;

const DashboardPageContainer = styled(PageContainer)`
  min-height: 100%;
  height: max-content;
`;

export const DashboardPage = () => {
  const { t } = useTranslation(['dashboard']);
  useAddBreadcrumb(t('dashboard:pageBreadcrumb'));
  const breakpointUpMd = useBreakpoint(up('md'));
  const breakpointUpXl = useBreakpoint(up('xl'));
  const { hasPermission } = useAuthorization();
  const isManager = hasPermission('platform:company.dashboard:access');

  const dashboardLayoutConfig = useMemo(
    () => ({
      unansweredConversations: isManager ? { xl: 8, xs: 24 } : { xl: 6, xs: 24 },
      smallCard: isManager ? { xl: 4, md: 6, xs: 24 } : { xl: 6, md: 8, xs: 24 },
      newContactAcquisition: isManager ? { xl: 4, md: 6, xs: 24 } : undefined,
      contactsToFollowUp: isManager
        ? { xs: 24, md: 12, xl: 8 }
        : { xs: 24, md: 12, xl: 8 },
      newContacts: isManager ? { xs: 24, md: 12, xl: 16 } : undefined,
      nextTasks: { xl: 8, md: 12, xs: 24 },
      nextAppointments: { xl: 8, md: 12, xs: 24 },
      assignedContacts: { xl: 8, md: 12, xs: 24 },
      agentPerformance: isManager ? { xs: 24 } : undefined,
      callAnalysis: isManager ? { xs: 24, xl: 16 } : { xs: 24, xl: 16 },
      unassignedContacts: isManager ? { xl: 8, md: 12, xs: 24 } : undefined,
    }),
    [isManager],
  );

  const tableRowGutter: [number, number] = useMemo(
    () => (breakpointUpMd ? [20, 20] : [15, 15]),
    [breakpointUpMd],
  );

  const unassignedContactsCardContent = useMemo(() => {
    if (!dashboardLayoutConfig.unassignedContacts) {
      return null;
    }

    return (
      <Col {...dashboardLayoutConfig.unassignedContacts}>
        <UnassignedContactsCard />
      </Col>
    );
  }, [dashboardLayoutConfig.unassignedContacts]);

  return (
    <DashboardPageContainer data-lgg-id="dashboard-page-container">
      <BreadcrumbBetaBadge />
      <DashboardFiltersContextProvider>
        <FiltersContainer>
          {breakpointUpMd ? (
            <FlexRow>
              <PeriodFilter />
            </FlexRow>
          ) : (
            <PeriodFilter />
          )}
          {isManager ? <DashboardUserFilter /> : null}
        </FiltersContainer>
        <CardsContainer>
          <Row gutter={tableRowGutter}>
            <Col {...dashboardLayoutConfig.unansweredConversations}>
              <UnansweredConversationsCard />
            </Col>
            <Col {...dashboardLayoutConfig.smallCard}>
              <SummaryAnsweredConversationsCard />
            </Col>
            <Col {...dashboardLayoutConfig.smallCard}>
              <MedianResponseTimeCard />
            </Col>
            {dashboardLayoutConfig.newContactAcquisition ? (
              <Col {...dashboardLayoutConfig.newContactAcquisition}>
                <NewContactAcquisitionCard />
              </Col>
            ) : null}
            <Col {...dashboardLayoutConfig.smallCard}>
              <ClosedContactsCard />
            </Col>
            <Col {...dashboardLayoutConfig.contactsToFollowUp}>
              <ContactsToFollowUpCard variant={isManager ? 'regular' : 'agent'} />
            </Col>
            {dashboardLayoutConfig.newContacts ? (
              <Col {...dashboardLayoutConfig.newContacts}>
                <NewContactsCard />
              </Col>
            ) : null}
            <Col {...dashboardLayoutConfig.nextTasks}>
              <NextTasksCard />
            </Col>
            <Col {...dashboardLayoutConfig.nextAppointments}>
              <NextAppointmentsCard />
            </Col>
            <Col {...dashboardLayoutConfig.assignedContacts}>
              <AssignedContactsCard />
            </Col>
            {!breakpointUpXl && breakpointUpMd ? unassignedContactsCardContent : null}
            {dashboardLayoutConfig.agentPerformance ? (
              <Col {...dashboardLayoutConfig.agentPerformance}>
                <AgentPerformanceCard />
              </Col>
            ) : null}
            <Col {...dashboardLayoutConfig.callAnalysis}>
              <CallAnalysisCard />
            </Col>
            {breakpointUpXl || !breakpointUpMd ? unassignedContactsCardContent : null}
          </Row>
        </CardsContainer>
      </DashboardFiltersContextProvider>
    </DashboardPageContainer>
  );
};
